import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { AssetLocation, DEFAULT_ASSET_LOCATION } from '../models/asset-browser.model';

@Injectable()
export class AssetNavigationService {
    private readonly locationSubject = new BehaviorSubject<AssetLocation>(this.toImmutableLocation(DEFAULT_ASSET_LOCATION));
    private readonly history: AssetLocation[] = [this.locationSubject.value];
    private historyIndex = 0;

    public readonly location$: Observable<AssetLocation> = this.locationSubject.asObservable();

    public get snapshot(): AssetLocation {
        return this.locationSubject.value;
    }

    public go(location: AssetLocation): void {
        const nextLocation = this.toImmutableLocation(location);

        if (this.areEqual(this.snapshot, nextLocation)) {
            return;
        }

        this.history.splice(this.historyIndex + 1);
        this.history.push(nextLocation);
        this.historyIndex = this.history.length - 1;
        this.locationSubject.next(nextLocation);
    }

    public reset(location: AssetLocation): void {
        const nextLocation = this.toImmutableLocation(location);
        this.history.splice(0, this.history.length, nextLocation);
        this.historyIndex = 0;
        this.locationSubject.next(nextLocation);
    }

    public back(): void {
        if (this.historyIndex === 0) {
            return;
        }

        this.historyIndex -= 1;
        this.locationSubject.next(this.history[this.historyIndex]);
    }

    public forward(): void {
        if (this.historyIndex === this.history.length - 1) {
            return;
        }

        this.historyIndex += 1;
        this.locationSubject.next(this.history[this.historyIndex]);
    }

    public up(): void {
        if (!this.snapshot.folderPath.length) {
            return;
        }

        this.go({ ...this.snapshot, folderPath: this.snapshot.folderPath.slice(0, -1) });
    }

    private toImmutableLocation(location: AssetLocation): AssetLocation {
        return Object.freeze({
            ...location,
            folderPath: Object.freeze([...location.folderPath]) as string[],
            filters: Object.freeze({ ...location.filters }),
            sort: Object.freeze({ ...location.sort }),
        });
    }

    private areEqual(left: AssetLocation, right: AssetLocation): boolean {
        return (
            left.scope === right.scope &&
            left.projectId === right.projectId &&
            left.collection === right.collection &&
            left.search === right.search &&
            left.filters.type === right.filters.type &&
            left.sort.by === right.sort.by &&
            left.sort.direction === right.sort.direction &&
            left.viewMode === right.viewMode &&
            left.folderPath.length === right.folderPath.length &&
            left.folderPath.every((folder, index) => folder === right.folderPath[index])
        );
    }
}
